import _styled, { default as _styled2, default as _styled3, default as _styled4, default as _styled5, default as _styled6, default as _styled7, default as _styled8, default as _styled9 } from "styled-components";
import React from "react";
import { graphql, useFragment, type FragmentType } from "~/gql/runtime";
import { takeFirst, stripHtmlTags } from "~/features/app/utils";
import { Media } from "~/features/ui/media";
import { Headline } from "~/features/ui/headline";
import { IconButton } from "~/features/ui/icon-button";
import Plus from "~/public/icons/plus.svg";
const fragment = graphql(`
  fragment InfoCardsCard on blocks_infoCardsCard_BlockType {
    title: baseText
    media: baseMedia {
      ...Media
    }
    description: baseRichText
    url: baseUrl
  }
`);
type Props = FragmentType<typeof fragment> & {
  ratio: "3/2" | "16/9";
};
export type InfoCardsCardViewProps = {
  description?: React.ReactNode;
  media?: React.ReactNode;
  ratio: "3/2" | "16/9";
  title?: string | null;
  url?: string | null;
};
export function InfoCardsCardView({
  description,
  media,
  title,
  url
}: InfoCardsCardViewProps) {
  const content = <_StyledDiv>
      <_StyledDiv2>
        <_StyledDiv3>
          <IconButton as="div" icon={{
          component: Plus,
          style: {
            "transitionProperty": "transform",
            "transitionDuration": "250ms",
            "transitionTimingFunction": "ease-in-out"
          },
          onHoverStyle: {
            "--tw-rotate": "90deg",
            "transform": "translate(var(--tw-translate-x), var(--tw-translate-y)) rotate(var(--tw-rotate)) skewX(var(--tw-skew-x)) skewY(var(--tw-skew-y)) scaleX(var(--tw-scale-x)) scaleY(var(--tw-scale-y))"
          }
        }} size="small" />
        </_StyledDiv3>
        <_StyledDiv4>
          {media}
        </_StyledDiv4>
      </_StyledDiv2>

      <_StyledDiv5>
        {title && <_StyledHeadline type="h3" text={title} />}

        {description && <_StyledDiv6>{description}</_StyledDiv6>}
      </_StyledDiv5>
    </_StyledDiv>;
  if (url) {
    return <_StyledA href={url} target="_blank" rel="noopener noreferrer" className="group">
        {content}
      </_StyledA>;
  }
  return <_StyledDiv7 className="group">
      {content}
    </_StyledDiv7>;
}
export function Card(props: Props) {
  const {
    ratio,
    ...rest
  } = props;
  const {
    title,
    media,
    description,
    url
  } = useFragment(fragment, rest);
  return <InfoCardsCardView description={description ? stripHtmlTags(description) : undefined} media={<Media {...takeFirst(media)} imageProps={{
    ratio: "1/1",
    fit: "cover",
    rounded: false
  }} />} ratio={ratio} title={title} url={url} />;
}
var _StyledDiv = _styled("div").withConfig({
  componentId: "sc-sf-1akelty-0"
})({
  "display": "flex",
  "gap": "var(--spacing-md)",
  "borderRadius": "4px",
  "backgroundColor": "var(--bg-primary)",
  "padding": "var(--spacing-sm)"
});
var _StyledDiv2 = _styled2("div").withConfig({
  componentId: "sc-sf-1akelty-1"
})({
  "position": "relative",
  "height": "9.6rem",
  "width": "9.6rem",
  "overflow": "hidden",
  "borderRadius": "2px",
  "@media (min-width: 1024px)": {
    "height": "146px",
    "width": "146px"
  }
});
var _StyledDiv3 = _styled3("div").withConfig({
  componentId: "sc-sf-1akelty-2"
})({
  "position": "absolute",
  "bottom": "0.4rem",
  "right": "0.4rem",
  "zIndex": "10",
  "display": "flex",
  "alignItems": "flex-end",
  "justifyContent": "flex-end",
  "gap": "0.4rem",
  "opacity": "0",
  "transitionProperty": "opacity",
  "transitionDuration": "250ms",
  ".group:hover &": {
    "opacity": "1"
  }
});
var _StyledDiv4 = _styled4("div").withConfig({
  componentId: "sc-sf-1akelty-3"
})({
  "height": "9.6rem",
  "width": "9.6rem",
  "transitionProperty": "transform",
  "transitionDuration": "250ms",
  "transitionTimingFunction": "ease-in-out",
  ".group:hover &": {
    "--tw-scale-x": "1.25",
    "--tw-scale-y": "1.25",
    "transform": "translate(var(--tw-translate-x), var(--tw-translate-y)) rotate(var(--tw-rotate)) skewX(var(--tw-skew-x)) skewY(var(--tw-skew-y)) scaleX(var(--tw-scale-x)) scaleY(var(--tw-scale-y))"
  },
  "@media (min-width: 1024px)": {
    "height": "146px",
    "width": "146px"
  }
});
var _StyledDiv5 = _styled5("div").withConfig({
  componentId: "sc-sf-1akelty-4"
})({
  "display": "flex",
  "flex": "1 1 0%",
  "flexDirection": "column",
  "@media (min-width: 1024px)": {
    "paddingTop": "var(--spacing-sm)",
    "paddingBottom": "var(--spacing-sm)"
  }
});
var _StyledHeadline = _styled6(Headline).withConfig({
  componentId: "sc-sf-1akelty-5"
})({
  "marginBottom": "var(--spacing-md)",
  "fontSize": "2rem",
  "lineHeight": "1.3",
  "letterSpacing": "0.02rem",
  "fontWeight": "400",
  "color": "var(--text-primary)",
  "@media (min-width: 1024px)": {
    "fontSize": "2.4rem",
    "lineHeight": "1.3",
    "letterSpacing": "0.024rem",
    "fontWeight": "400"
  }
});
var _StyledDiv6 = _styled7("div").withConfig({
  componentId: "sc-sf-1akelty-6"
})({
  "marginTop": "auto",
  "fontSize": "1.6rem",
  "lineHeight": "1.5",
  "fontWeight": "400",
  "color": "var(--text-transparent)"
});
var _StyledA = _styled8("a").withConfig({
  componentId: "sc-sf-1akelty-7"
})({
  "display": "flex",
  "height": "100%",
  "cursor": "pointer",
  "flexDirection": "column",
  "transitionProperty": "transform",
  "transitionDuration": "250ms",
  "transitionTimingFunction": "ease-in-out"
});
var _StyledDiv7 = /*#__PURE__*/_styled9("div").withConfig({
  displayName: "card___StyledDiv7",
  componentId: "sc-14ofs25-0"
})({
  "display": "flex",
  "height": "100%",
  "flexDirection": "column"
});